/**
 * User-administration console (sysadmin only).
 *
 * All accounts in one table, pending requests pinned to the top with
 * Approve / Reject; per-row Reset password, Activate/Deactivate, and Delete
 * behind a type-the-email confirmation. "New user" creates an active account
 * directly and reveals its temporary password once. Every mutation refetches
 * the list so the table always reflects committed server state.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import LockResetIcon from "@mui/icons-material/LockReset";
import BlockIcon from "@mui/icons-material/Block";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BusinessIcon from "@mui/icons-material/Business";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import { UserCustomersDialog } from "../components/UserCustomersDialog";
import { NewUserDialog } from "../components/NewUserDialog";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "../components/ui/PageHeader";
import { useAuth } from "../auth/useAuth";
import { DataTable } from "../components/ui/DataTable";
import { EmptyState } from "../components/ui/EmptyState";
import { ErrorAlert } from "../components/ui/ErrorAlert";
import { useApiQuery } from "../hooks/useApiQuery";
import { useToast } from "../hooks/useToast";
import { api, ApiError, type AdminUser } from "../lib/api";
import { usePageTitle } from "../hooks/usePageTitle";

const STATUS_COLOR: Record<AdminUser["status"], "default" | "success" | "warning" | "error"> = {
  pending: "warning",
  active: "success",
  inactive: "default",
  deleted: "error",
};

export function AdminUsersPage() {
  const { t } = useTranslation();
  usePageTitle(t("admin.title"));
  // under OIDC this console describes something it does not own: the
  // accounts and the roles live in Entra. Say so rather than show an
  // empty-looking table of local rows as if it were the access list.
  const { mode } = useAuth();
  const sso = mode === "oidc";
  const toast = useToast();
  const qc = useQueryClient();
  const query = useApiQuery(["admin", "users"], api.adminUsers);
  const customersQuery = useApiQuery(["admin", "customers"], api.adminCustomers);
  const allCustomers = useMemo(() => customersQuery.data?.customers ?? [], [customersQuery.data]);
  const [assigning, setAssigning] = useState<AdminUser | null>(null);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ user: AdminUser; kind: "delete" } | null>(null);
  const [tempPassword, setTempPassword] = useState<{ email: string; value: string } | null>(null);

  const rows = useMemo(() => query.data?.users ?? [], [query.data]);
  const pendingCount = useMemo(() => rows.filter((u) => u.status === "pending").length, [rows]);

  const refetch = () => qc.invalidateQueries({ queryKey: ["admin", "users"] });

  async function run(id: string, fn: () => Promise<unknown>, ok: string) {
    setBusyId(id);
    try {
      await fn();
      toast(ok, "success");
      await refetch();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t("admin.actionFailed"), "error");
    } finally {
      setBusyId(null);
    }
  }

  const columns = [
    { key: "name", label: t("admin.colName") },
    { key: "role", label: t("admin.colRole") },
    { key: "status", label: t("admin.colStatus") },
    { key: "customers", label: t("customers.colCustomers") },
    { key: "lastLogin", label: t("admin.colLastLogin") },
    { key: "actions", label: t("admin.colActions"), align: "right" as const },
  ];

  if (query.isError) {
    return <ErrorAlert error={query.error} onRetry={() => query.refetch()} />;
  }

  return (
    <Stack spacing={3}>
      <PageHeader
        title={t("admin.title")}
        subtitle={sso ? t("admin.ssoSubtitle") : t("admin.subtitle")}
        extra={
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
            <Button
              size="small"
              variant="contained"
              startIcon={<PersonAddAlt1Icon />}
              onClick={() => setCreating(true)}
            >
              {t("admin.newUser")}
            </Button>
            {pendingCount > 0 && (
              <Chip
                color="warning"
                size="small"
                sx={{ fontWeight: 600 }}
                label={t("admin.pendingBadge", { count: pendingCount })}
              />
            )}
          </Stack>
        }
      />
      {sso && (
        <Typography variant="subtitle2" color="text.secondary">
          {t("admin.localAccountsTitle")}
        </Typography>
      )}

      {rows.length === 0 && !query.isLoading ? (
        <EmptyState message={t("admin.empty")} />
      ) : (
        <DataTable columns={columns}>
          {rows.map((u) => {
            const busy = busyId === u.id;
            const isPending = u.status === "pending";
            const isDeleted = u.status === "deleted";
            return (
              <TableRow key={u.id} hover selected={isPending}>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {u.full_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {u.email}
                  </Typography>
                </TableCell>
                <TableCell>{t(`auth.role.${u.role}`, u.role)}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Chip
                      size="small"
                      color={STATUS_COLOR[u.status]}
                      label={t(`admin.status.${u.status}`, u.status)}
                    />
                    {u.locked && (
                      <Chip size="small" variant="outlined" color="warning" label={t("admin.locked")} />
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {u.all_customers
                      ? t("customers.assignAll")
                      : u.customer_ids.length === 0
                        ? t("customers.assignNone")
                        : u.customer_ids
                            .map((id) => allCustomers.find((c) => c.id === id)?.name ?? id)
                            .join(", ")}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {u.last_login_at
                      ? new Date(u.last_login_at).toLocaleString()
                      : t("admin.never")}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {isPending ? (
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<CheckIcon />}
                        disabled={busy}
                        onClick={() =>
                          run(u.id, () => api.adminApprove(u.id, "operator"), t("admin.approved"))
                        }
                      >
                        {t("admin.approve")}
                      </Button>
                      <Button
                        size="small"
                        color="inherit"
                        startIcon={<CloseIcon />}
                        disabled={busy}
                        onClick={() => run(u.id, () => api.adminReject(u.id, ""), t("admin.rejected"))}
                      >
                        {t("admin.reject")}
                      </Button>
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title={t("customers.assign")}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={busy || isDeleted}
                            onClick={() => setAssigning(u)}
                          >
                            <BusinessIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t("admin.resetPassword")}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={busy || isDeleted}
                            onClick={() =>
                              run(
                                u.id,
                                async () => {
                                  const r = await api.adminResetPassword(u.id);
                                  setTempPassword({ email: u.email, value: r.temporary_password });
                                },
                                t("admin.passwordReset"),
                              )
                            }
                          >
                            <LockResetIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {u.status === "active" ? (
                        <Tooltip title={t("admin.deactivate")}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={busy}
                              onClick={() =>
                                run(u.id, () => api.adminDeactivate(u.id), t("admin.deactivated"))
                              }
                            >
                              <BlockIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : (
                        <Tooltip title={t("admin.activate")}>
                          <span>
                            <IconButton
                              size="small"
                              color="success"
                              disabled={busy || isDeleted}
                              onClick={() =>
                                run(u.id, () => api.adminActivate(u.id), t("admin.activated"))
                              }
                            >
                              <PlayArrowIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title={t("admin.delete")}>
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={busy || isDeleted}
                            onClick={() => setConfirm({ user: u, kind: "delete" })}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </DataTable>
      )}

      {/* Delete confirmation — type the email to arm the button. */}
      <DeleteConfirmDialog
        target={confirm?.user ?? null}
        onClose={() => setConfirm(null)}
        onConfirm={async (user) => {
          setConfirm(null);
          await run(user.id, () => api.adminDelete(user.id), t("admin.deleted"));
        }}
      />

      {creating && (
        <NewUserDialog
          customers={allCustomers}
          onClose={() => setCreating(false)}
          onCreated={async (email, temporary) => {
            setCreating(false);
            setTempPassword({ email, value: temporary });
            toast(t("admin.created"), "success");
            await refetch();
          }}
        />
      )}

      {assigning && (
        <UserCustomersDialog
          user={assigning}
          customers={allCustomers}
          onClose={() => setAssigning(null)}
          onSaved={async () => {
            setAssigning(null);
            toast(t("customers.assigned"), "success");
            await refetch();
          }}
        />
      )}

      {/* One-time temporary password reveal. */}
      <Dialog open={Boolean(tempPassword)} onClose={() => setTempPassword(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("admin.tempPasswordTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("admin.tempPasswordBody", { email: tempPassword?.email })}
          </DialogContentText>
          <Alert severity="warning" sx={{ mb: 1 }}>
            {t("admin.tempPasswordWarning")}
          </Alert>
          <TextField
            value={tempPassword?.value ?? ""}
            fullWidth
            InputProps={{ readOnly: true, sx: { fontFamily: "monospace", fontWeight: 700 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (tempPassword) void navigator.clipboard?.writeText(tempPassword.value);
            }}
          >
            {t("admin.copy")}
          </Button>
          <Button variant="contained" onClick={() => setTempPassword(null)}>
            {t("common.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function DeleteConfirmDialog({
  target,
  onClose,
  onConfirm,
}: {
  target: AdminUser | null;
  onClose: () => void;
  onConfirm: (user: AdminUser) => void;
}) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState("");
  const armed = target !== null && typed.trim().toLowerCase() === target.email.toLowerCase();

  return (
    <Dialog
      open={target !== null}
      onClose={() => {
        setTyped("");
        onClose();
      }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>{t("admin.deleteTitle")}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <DialogContentText>
          {t("admin.deleteBody", { name: target?.full_name, email: target?.email })}
        </DialogContentText>
        <TextField
          label={t("admin.deleteConfirmLabel")}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={target?.email}
          autoFocus
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <Button
          color="inherit"
          onClick={() => {
            setTyped("");
            onClose();
          }}
        >
          {t("common.close")}
        </Button>
        <Button
          color="error"
          variant="contained"
          disabled={!armed}
          onClick={() => {
            if (target) onConfirm(target);
            setTyped("");
          }}
        >
          {t("admin.delete")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
